'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { buildLoginRedirectUrl, getAuthSession, readSharedLogoutReason, subscribeAuthSession } from '@/lib/authSession';
import { notifyDesktopLogout } from '@/lib/desktopBridge';
import { isPortalBuild } from '@/lib/appTarget';
import { startPortalAuthorize } from '@/lib/portalAuth';
import { SpinnerScreen } from '@/components/loading/Spinner';

type AuthGuardStatus = 'checking' | 'authenticated' | 'unauthenticated';

const loaderDelayMs = 150;

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const t = useTranslations('components.authGuard');
  const [status, setStatus] = useState<AuthGuardStatus>('checking');
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);
  const hasHandledRef = useRef(false);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const redirectToLogin = (options?: { excludeCli?: boolean; source?: 'same-tab' | 'storage' }) => {
      if (hasRedirectedRef.current) return;
      if (window.location.pathname.startsWith('/auth')) return;
      hasRedirectedRef.current = true;
      if (notifyDesktopLogout(readSharedLogoutReason())) return;
      const currentUrl = window.location.pathname + window.location.search;
      // Portal은 로그인 화면이 없다. web 인가 화면으로 복귀 경로를 들고 떠난다.
      if (isPortalBuild) {
        // 다른 탭 로그아웃을 자동 인가로 되돌리지 않는다. 초기 진입·401만 인가를 시작한다.
        if (options?.source === 'storage') {
          window.location.href = '/auth/logged-out';
          return;
        }
        void startPortalAuthorize(currentUrl);
        return;
      }
      // 다른 탭이 기록한 강제 로그아웃 사유를 함께 전달해, handle401Response()와 동일한 로그인 URL을 만든다.
      window.location.href = buildLoginRedirectUrl(currentUrl, options, readSharedLogoutReason());
    };

    let loaderTimeoutId: number | null = null;

    if (!hasHandledRef.current) {
      hasHandledRef.current = true;

      loaderTimeoutId = window.setTimeout(() => {
        setIsLoaderVisible(true);
      }, loaderDelayMs);

      const session = getAuthSession();
      if (!session) {
        setStatus('unauthenticated');
        redirectToLogin();
      } else {
        setStatus('authenticated');
        window.clearTimeout(loaderTimeoutId);
        loaderTimeoutId = null;
      }
    }

    // 다른 탭의 로그아웃(세션 null 전이)을 감지해 즉시 로그인 이동. /auth/* 페이지는 제외.
    // 명시적 로그아웃·탈퇴처럼 호출부가 이동을 소유하면 목적지가 확정되어 있으므로 여기서는 이동하지 않는다.
    const unsubscribe = subscribeAuthSession((next, context) => {
      if (next) return;
      setStatus('unauthenticated');
      if (context.callerHandlesRedirect) return;
      redirectToLogin({ excludeCli: true, source: context.source });
    });

    return () => {
      unsubscribe();
      if (loaderTimeoutId !== null) {
        window.clearTimeout(loaderTimeoutId);
      }
    };
  }, []);

  if (status !== 'authenticated') {
    if (!isLoaderVisible) return null;

    return (
      <div className="flex min-h-screen items-center justify-center bg-base-200">
        <SpinnerScreen label={t('loading')} />
      </div>
    );
  }

  return <>{children}</>;
}
